import { useCallback } from "react";

interface GpsAvailable {
  readonly status: "available";
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracy: number;
}

interface GpsDenied {
  readonly status: "denied";
}

interface GpsTimeout {
  readonly status: "timeout";
}

interface GpsUnavailable {
  readonly status: "unavailable";
}

export type GpsResult = GpsAvailable | GpsDenied | GpsTimeout | GpsUnavailable;

const GPS_TIMEOUT_MS = 10_000;

function capturePosition(): Promise<GpsResult> {
  if (!navigator.geolocation) {
    return Promise.resolve({ status: "denied" as const });
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          status: "available",
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            resolve({ status: "denied" });
            break;
          case error.TIMEOUT:
            resolve({ status: "timeout" });
            break;
          default:
            resolve({ status: "unavailable" });
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: GPS_TIMEOUT_MS,
        maximumAge: 30_000,
      },
    );
  });
}

export function useGpsPosition() {
  const capture = useCallback(() => capturePosition(), []);
  return { capture };
}
